/**
 * CampaignInputsGate — Sprint UI Completion
 * ==========================================
 *
 * Visible gate that renders the 3 Campaign Strategy inputs
 * (Social Content Strategy, Distribution Strategy, Influencer
 * Suggestions) before Business Plan Final. Backend at
 * `/api/projects/{id}/campaign-inputs` is the single source of
 * truth. Business Plan run/approve is 409-blocked on the backend
 * (`campaign_inputs_not_ready`) until every row is either reviewed
 * OR skipped-with-reason. This component makes that requirement
 * VISIBLE + ACTIONABLE for the operator.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const _LABEL_FALLBACK = {
    social_content_strategy: "Social Content Strategy",
    distribution_strategy:   "Distribution Strategy",
    influencer_suggestions:  "Influencer Suggestions",
};

const _STATE_BADGE = {
    reviewed: { text: "REVIEWED", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
    skipped:  { text: "SKIPPED",  cls: "bg-amber-500/10 text-amber-300 border-amber-500/40" },
    missing:  { text: "MISSING",  cls: "bg-red-500/10 text-red-300 border-red-500/40" },
};

function InputRow({ keyName, label, row, onReview, onSkip, busy }) {
    const state = row.reviewed ? "reviewed"
                : (row.skip_reason ? "skipped" : "missing");
    const badge = _STATE_BADGE[state];
    return (
        <div
            data-testid={`campaign-input-row-${keyName}`}
            className="flex items-center justify-between gap-3 py-2 px-3 border border-zinc-900 rounded-sm bg-[#0a0a0a]"
        >
            <div className="flex items-center gap-3 min-w-0">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${badge.cls}`}
                      data-testid={`campaign-input-badge-${keyName}`}>
                    {badge.text}
                </span>
                <span className="text-xs text-zinc-200 truncate">{label}</span>
                {row.skip_reason && (
                    <span className="text-[10px] font-mono text-amber-300/70 italic truncate max-w-[240px]"
                          title={row.skip_reason}>
                        skip: {row.skip_reason}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    onClick={() => onReview(keyName)}
                    disabled={busy || row.reviewed}
                    data-testid={`campaign-input-review-${keyName}`}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase rounded-sm border border-emerald-700 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {row.reviewed ? "Reviewed" : "Review"}
                </button>
                <button
                    onClick={() => onSkip(keyName, label)}
                    disabled={busy}
                    data-testid={`campaign-input-skip-${keyName}`}
                    className="px-2.5 py-1 text-[10px] font-mono uppercase rounded-sm border border-amber-700 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Skip…
                </button>
            </div>
        </div>
    );
}

export default function CampaignInputsGate({ projectId, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/projects/${projectId}/campaign-inputs`);
            setData(r.data);
        } catch (e) {
            const msg = e?.userMessage || e?.response?.data?.detail || e?.message || "Failed";
            toast.error(`Campaign inputs load failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { if (projectId) load(); }, [projectId, load]);

    const labels = (data && data.labels) || _LABEL_FALLBACK;
    const inputs = (data && data.inputs) || {};
    const keys = Object.keys(labels);
    const missing = (data && data.missing_campaign_inputs) || [];
    const allReady = !!(data && data.all_ready);

    const review = async (keyName) => {
        setBusy(true);
        try {
            const r = await api.patch(`/projects/${projectId}/campaign-inputs/${keyName}/review`);
            toast.success(`${labels[keyName] || keyName} marked reviewed.`);
            // Workflow Hardening 2 — re-fetch from the backend authority.
            // (The old optimistic setData(prev=>…) silently no-oped when the
            // initial load had failed, leaving stale MISSING badges.)
            await load();
            onChanged && onChanged(r.data);
        } catch (e) {
            const msg = e?.userMessage || e?.response?.data?.detail || "Failed";
            toast.error(`Review failed: ${msg}`);
        } finally { setBusy(false); }
    };

    const skip = async (keyName, label) => {
        const reason = (window.prompt(
            `Skip "${label}" with reason:\n\nThe backend requires a non-empty reason to skip a Campaign Strategy input.`,
            "",
        ) || "").trim();
        if (!reason) {
            toast.error("Skip requires a non-empty reason.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post(
                `/projects/${projectId}/campaign-inputs/${keyName}/skip`,
                { reason },
            );
            toast.success(`${label} skipped: "${reason}".`);
            await load();
            onChanged && onChanged(r.data);
        } catch (e) {
            const msg = e?.userMessage || e?.response?.data?.detail || "Failed";
            toast.error(`Skip failed: ${msg}`);
        } finally { setBusy(false); }
    };

    const missingLabels = useMemo(
        () => missing.map((k) => labels[k] || k),
        [missing, labels],
    );

    // Sprint UI — derive the display count directly from the rendered
    // rows so the chip stays honest even if the fetch is racing / fails.
    const displayMissingCount = useMemo(() => {
        return keys.filter((k) => {
            const r = inputs[k] || {};
            return !(r.reviewed || (r.skip_reason || "").trim());
        }).length;
    }, [keys, inputs]);

    return (
        <div
            data-testid="campaign-inputs-gate"
            className="border border-zinc-800 rounded-sm p-4 mt-4 bg-[#0a0a0a]"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="label-micro">Campaign Strategy Inputs</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${
                        allReady
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                          : "bg-red-500/10 text-red-300 border-red-500/40"
                    }`} data-testid="campaign-inputs-gate-status">
                        {allReady ? "READY — BUSINESS PLAN UNLOCKED" : `BUSINESS PLAN LOCKED — ${displayMissingCount} MISSING`}
                    </span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="campaign-inputs-refresh"
                    className="text-[10px] font-mono uppercase text-zinc-400 hover:text-zinc-200"
                >
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>
            <p className="text-[11px] text-zinc-400 mb-3 leading-relaxed">
                Business Plan cannot run or be approved until each input below
                is either <span className="text-emerald-300 font-semibold">reviewed</span> or
                {" "}<span className="text-amber-300 font-semibold">skipped with a reason</span>.
                {!allReady && missingLabels.length > 0 && (
                    <> Missing: <span className="text-red-300 font-semibold">{missingLabels.join(", ")}</span>.</>
                )}
            </p>
            <div className="flex flex-col gap-2">
                {keys.map((k) => (
                    <InputRow
                        key={k}
                        keyName={k}
                        label={labels[k] || k}
                        row={inputs[k] || {}}
                        onReview={review}
                        onSkip={skip}
                        busy={busy || loading}
                    />
                ))}
            </div>
        </div>
    );
}
